import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MediaSize } from '../constants/consts';
import TaskPromise from '../services/TaskPromise';
import { useMediaSize } from './useMediaSize';

const LATEST_PROMPT_SELECTOR = '[data-message-type="human"]';
const PROMPT_TOP_OFFSET = 16;

/** Scrolls down toward the prompt's top, capped at the content end. Returns true once the prompt reached the top. */
const scrollPromptTowardTop = (el: HTMLElement, prompt: HTMLElement, onlyIfResponseFits = false) => {
    if (!prompt.isConnected) return true;
    const target = prompt.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - PROMPT_TOP_OFFSET;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const reachedTop = target <= maxScroll;
    if (onlyIfResponseFits && reachedTop) return false;
    const next = Math.min(target, maxScroll);
    if (next > el.scrollTop) el.scrollTo({ top: next, behavior: 'instant' });
    return reachedTop;
};

type ReturnType = {
    /**Performs scroll to bottom.*/
    scrollToBottom: () => void;
    /**Performs scroll to bottom after a delay. Default 100 */
    scrollToBottomWithDelay: (delay?: number) => Promise<void>;
    /**Performs scroll to bottom only if the current scroll position is not greater than the buffer */
    scrollToBottomWithBuffer: () => void;
    /**Performs scroll to bottom after a delay only if the current scroll position is not greater than the buffer. Default 100 */
    scrollToBottomWithBufferAndDelay: (delay?: number) => Promise<void>;
    /**Follows new content until the latest prompt reaches the top of the view, then stops so the response fills the viewport. */
    scrollToLatestPrompt: () => void;
    scrollBuffer: number;
    hasContentBelow: boolean;
};
export default function useScrollToBottom(element?: React.RefObject<HTMLDivElement>, buffer: 'auto' | number = 'auto'): ReturnType {
    const scrollBuffer = useMediaSize(ms => buffer !== 'auto' ? buffer : ms <= MediaSize.xs ? 120 : 80);

    const lastScrollPositionRef = useRef(0);
    const anchored = useRef(false);
    const pinnedPromptRef = useRef<HTMLElement | null>(null);
    const isPinFollowing = useRef(false);
    const [hasContentBelow, setHasContentBelow] = useState(false);
    const [el, setEl] = useState<HTMLDivElement | null>(null);

    // The ref target may mount after the first render (e.g. after loading), so re-check it on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        const current = element?.current ?? null;
        if (current !== el) setEl(current);
    });

    useLayoutEffect(() => {
        if (!el) {
            setHasContentBelow(false);
            return;
        }

        const updateContentBelow = () => {
            setHasContentBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
        };

        const handler = () => {
            updateContentBelow();
            const lastScrollPosition = lastScrollPositionRef.current;
            lastScrollPositionRef.current = el.scrollTop;


            const scrollBottomPosition = el.scrollTop + el.offsetHeight;
            const scrollHeight = el.scrollHeight - scrollBuffer;
            const scrolledToBottom = scrollBottomPosition >= scrollHeight;
            const scrollingUp = lastScrollPosition > el.scrollTop;

            anchored.current = !scrollingUp && scrolledToBottom;
            // Ignore upward moves caused by the browser clamping scrollTop when content/viewport changes.
            const isClampedToEnd = el.scrollHeight - el.clientHeight - el.scrollTop <= 1;
            if (scrollingUp && !isClampedToEnd) isPinFollowing.current = false;
        };

        // Short responses keep growing after the last chunk (feedback icons, help panel); reveal their end on resize.
        // Long responses are left to the per-chunk pinning so their start stays in view.
        const onResize = () => {
            if (isPinFollowing.current && pinnedPromptRef.current) {
                scrollPromptTowardTop(el, pinnedPromptRef.current, true);
            }
            updateContentBelow();
        };

        el.addEventListener('scroll', handler);
        handler();
        const resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(el);
        if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);

        return () => {
            el.removeEventListener('scroll', handler);
            resizeObserver.disconnect();
        };
    }, [el, scrollBuffer]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const scrollToBottom = useCallback(() => {
        const el = element?.current;
        if (!el) return;
        requestAnimationFrame(() => el.scrollTop = el.scrollHeight);
    }, [element]);

    const scrollToBottomWithDelay = useCallback(async (delay = 150) => {
        await TaskPromise.delay(delay);
        scrollToBottom();
    }, [scrollToBottom]);

    const scrollToBottomWithBuffer = useCallback(async () => {
        if (!anchored.current) return;
        scrollToBottom();
    }, [scrollToBottom]);

    const scrollToBottomWithBufferAndDelay = useCallback(async (delay = 100) => {
        await TaskPromise.delay(delay);
        scrollToBottomWithBuffer();
    }, [scrollToBottomWithBuffer]);

    const scrollToLatestPrompt = useCallback(() => {
        const el = element?.current;
        if (!el) return;

        const prompts = el.querySelectorAll<HTMLElement>(LATEST_PROMPT_SELECTOR);
        const prompt = prompts[prompts.length - 1];
        if (!prompt) return scrollToBottomWithBuffer();

        if (pinnedPromptRef.current !== prompt) {
            pinnedPromptRef.current = prompt;
            isPinFollowing.current = true;
        }
        if (!isPinFollowing.current) return;

        requestAnimationFrame(() => {
            if (isPinFollowing.current && scrollPromptTowardTop(el, prompt)) isPinFollowing.current = false;
        });
    }, [element, scrollToBottomWithBuffer]);

    return {
        scrollToBottom,
        scrollToBottomWithDelay,
        scrollToBottomWithBuffer,
        scrollToBottomWithBufferAndDelay,
        scrollToLatestPrompt,
        scrollBuffer,
        hasContentBelow
    };
}